import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import {
    AccountActions,
    AccountSelectors,
    DefaultModalComponent,
    DefaultModalConfig,
    LocalizationSelectors,
    ModalsService,
    ModulesSelectors,
} from '@serviceos-ng/core';
import { Observable, Subscription } from 'rxjs';
import { finalize, take, tap } from 'rxjs/operators';
import {
    SearchCountryField,
    CountryISO,
    PhoneNumberFormat,
} from 'ngx-intl-tel-input';

interface PhonesComponentConfig {
    title: string;
    empty_message: string;
    delete_button_title: string;
    add_button_title: string;
    add_form: {
        title: string;
        phone_input_placeholder: {
            gb: string;
            ie: string;
            au: string;
            hun: string;
            us: string;
        };
        phone_input_error: string;
        confirm_button_title: string;
    };
}

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'serviceos-web-phones-form',
    templateUrl: './phones-form.component.html',
    styleUrls: [
        './phones-form.component.scss',
        './../../../../../../../node_modules/intl-tel-input/build/css/intlTelInput.css',
    ],
})
export class PhonesFormComponent implements OnInit {
    @Select(AccountSelectors.getPhones)
    public phones!: Observable<
        | Array<{
              default: boolean;
              id: number;
              value: string;
              sort: number;
          }>
        | null
        | []
    >;

    @Select(
        ModulesSelectors.getComponentFromModule(
            'account',
            'account_settings_screen.contact_details.phones'
        )
    )
    phone$!: Observable<PhonesComponentConfig | null>;
    phoneConfig: PhonesComponentConfig | null = null;

    @Select(ModulesSelectors.getSetting('country_code'))
    country_code$!: Observable<CountryISO>;

    selectedCountryICO = CountryISO.UnitedKingdom;

    public subscriptions: Subscription[] = [];
    public userPhonesForm: FormGroup;
    public userPhonesFormExpanded = true;
    public showLoader = false;
    public formErrorMessage = '';
    separateDialCode = true;
    customPlaceholder = '';
    SearchCountryField = SearchCountryField;
    CountryISO = CountryISO;
    PhoneNumberFormat = PhoneNumberFormat;
    preferredCountries: CountryISO[] = [
        CountryISO.UnitedStates,
        CountryISO.UnitedKingdom,
        CountryISO.Australia,
    ];
    private confirmDeleteModalConfig: any = null;

    public documentDirection = '';

    constructor(
        private _fb: FormBuilder,
        private _store: Store,
        private _modalsService: ModalsService
        ) {
        this.userPhonesForm = this._fb.group({
            phone: new FormControl(undefined, [Validators.required]),
        });
    }

    ngOnInit(): void {

        this._store.select(LocalizationSelectors.getCurrentDirection()).subscribe(
            (currentDirection) => {
                if (currentDirection) {
                    this.documentDirection = currentDirection;
                }
            }
        )

        this.confirmDeleteModalConfig = this._store.selectSnapshot(ModulesSelectors.getComponentFromModule('account', 'confirm_delete_modal'));

        this.subscriptions.push(
            this.country_code$.subscribe((res: any) => {
                if (typeof res == 'string') {
                    const countries = Object.values(CountryISO);
                    res = res.toLocaleLowerCase();
                    if (countries.includes(res)) {
                        this.countryChange(res);
                        this.selectedCountryICO = res;
                    }
                }
            })
        );

        this.subscriptions.push(
            this.phone$.subscribe((res) => {
                if (res) {
                    this.phoneConfig = res;
                } else {
                    this.phoneConfig = null;
                }
                this.countryChange(this.selectedCountryICO);
            })
        );

        this.subscriptions.push(
            this.phones
                .pipe(
                    tap((phones) => {
                        this.userPhonesFormExpanded =
                            phones && phones.length ? false : true;

                        // Place default address first
                        // if (addresses && addresses.length && addresses.length !== 1) {
                        // 	addresses = addresses.sort((currentAddress, nextAddress) => {
                        // 		if (currentAddress.default) return 1;
                        // 		else return -1;
                        // 	});
                        // }
                    })
                )
                .subscribe()
        );
    }

    /**
     * Format phone
     * @param phone
     * @returns
     */
    public formatPhone(phone: any): string {
        return phone; // todo ?
    }

    /**
     * Address form submit handler
     */
    public async formSubmit() {
        if (this.userPhonesForm.valid) {
            this.showLoader = true;

            const payload: any = {
                value: this.userPhonesForm.value.phone.e164Number,
            };

            this._store
                .dispatch(new AccountActions.CreatePhone(payload))
                .pipe(
                    finalize(() => {
                        this.showLoader = false;
                    })
                )
                .subscribe(
                    (res) => {
                        this.formErrorMessage = '';

                        this.userPhonesForm = this._fb.group({
                            phone: [
                                '',
                                [Validators.required, Validators.minLength(1)],
                            ],
                        });
                    },
                    (error) => {
                        this.formErrorMessage = error?.error[0]?.message;
                    }
                );
        } else {
            const formControlsNames: Array<string> = Object.keys(
                this.userPhonesForm.controls
            );

            // Manually trigger reactive form validation
            for (
                let formControlNameIndex = 0;
                formControlNameIndex < formControlsNames.length;
                formControlNameIndex++
            ) {
                const formControlName: string =
                    formControlsNames[formControlNameIndex];

                this.userPhonesForm.controls[formControlName].markAsTouched();
                this.userPhonesForm.controls[formControlName].markAsDirty();
            }
        }
    }

    /**
     * Delete phone handler
     * @param phoneId
     */
    public deletePhone(phoneId: number): void {
        const dialogRef = this._modalsService.open(
            new DefaultModalConfig(
                '', 
                this.confirmDeleteModalConfig.title,
                [
                    {
                        title: this.confirmDeleteModalConfig.cancel_button_title,
                        type: 'close',
                    },
                    {
                        title: this.confirmDeleteModalConfig.confirm_button_title,
                        type: 'confirm',
                    }
                ],
                'warning'
            )
        );

        dialogRef.close$.pipe(take(1)).subscribe((res) => {
            if (res === 'confirm') {
                this.showLoader = true;

                this._store
                    .dispatch(new AccountActions.DeletePhone({ id: phoneId }))
                    .pipe(
                        finalize(() => {
                            this.showLoader = false;
                        })
                    )
                    .subscribe(
                        (response) => {
                            this.formErrorMessage = '';
                        },
                        (error) => {
                            this.formErrorMessage = error?.error[0]?.message;
                        }
                    );
            }
        });
    }

    /**
     * Mark address as default
     * @param address
     */
    public setAsDefault(phone: any): void {
        this.showLoader = true;

        const payload: any = {
            id: phone.id,
            value: phone.value,
            default: true,
        };

        this._store
            .dispatch(new AccountActions.MarkPhoneAsDefault(payload))
            .pipe(
                finalize(() => {
                    this.showLoader = false;
                })
            )
            .subscribe(
                (response) => {
                    this.formErrorMessage = '';
                },
                (error) => {
                    this.formErrorMessage = error?.error[0]?.message;
                }
            );
    }

    ngOnDestroy(): void {
        this.subscriptions.map((el) => el.unsubscribe());
    }

    public countryChange(countryIso: string) {
        if (this.phoneConfig?.add_form?.phone_input_placeholder[countryIso]) {
            this.customPlaceholder = this.phoneConfig?.add_form?.phone_input_placeholder[
                countryIso
            ];
        } else {
            this.customPlaceholder = '';
        }
    }

    get phone() {
        return this.userPhonesForm.get('phone');
    }
}
